import axios from "axios";
import { BLOCKS_API_URL, PROJECT_KEY } from "@/src/services/blocks/config";
import { refreshSession } from "@/src/services/blocks/sso";
import {
  isAiTokenExpired,
  isPreSessionRequest,
  isRestTokenExpired,
} from "./auth-error";
import { handleAuthFailure, MAX_REFRESH_ATTEMPTS } from "./token-refresh";

/**
 * REST client for Blocks v4.
 *
 * v4 authenticates with the hosted SSO session cookie, so there is no
 * `Authorization: Bearer` header and no JS-held token to inject —
 * `withCredentials` carries the session instead. On 401 we renew via
 * `POST /iam/v4/oidc/token` (which rotates the cookies) and retry once.
 */
const apiClient = axios.create({
  baseURL: BLOCKS_API_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    "x-blocks-key": PROJECT_KEY,
  },
});

apiClient.interceptors.request.use(
  (config) => {
    config.headers = config.headers ?? {};
    config.headers["x-blocks-key"] = PROJECT_KEY;
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Some call sites pass an absolute URL to a non-Blocks service (e.g. the post
 * embedding service). A 401 from those must not trigger a Blocks session
 * refresh — only same-host Blocks calls should.
 */
const isBlocksRequest = (url: string | undefined): boolean => {
  if (!url) return false;
  if (!/^https?:\/\//i.test(url)) return true; // relative → resolved against baseURL
  return !!BLOCKS_API_URL && url.startsWith(BLOCKS_API_URL);
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      !originalRequest ||
      originalRequest.url?.includes("/oidc/token") ||
      isPreSessionRequest(originalRequest.url) ||
      originalRequest._skipAuthRefresh ||
      !isBlocksRequest(originalRequest.url)
    ) {
      // Rejected untouched, so the caller still has `error.response` — the
      // status and the `{error, error_description}` body the UI renders.
      return Promise.reject(error);
    }

    const isAiEndpoint = originalRequest.url?.includes("/blocksai-api");
    const shouldRefresh = isAiEndpoint
      ? isAiTokenExpired(error.response?.data)
      : isRestTokenExpired(error.response);

    if (!shouldRefresh) {
      return Promise.reject(error);
    }

    originalRequest._refreshAttempts = originalRequest._refreshAttempts ?? 0;
    if (originalRequest._refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      // Attempts exhausted while still being refused. `refreshSession()` can
      // resolve without actually restoring anything, and that path used to just
      // reject — leaving the session neither renewed nor ended, so the app kept
      // firing doomed requests instead of returning the user to login.
      await handleAuthFailure();
      return Promise.reject(error);
    }
    originalRequest._refreshAttempts += 1;

    try {
      await refreshSession();
    } catch {
      // The hosted session is gone (refresh cookie expired or revoked) — there
      // is no token to inspect under v4, so any refresh failure ends the session.
      await handleAuthFailure();
      // Reject with the ORIGINAL error, not the refresh failure: the refresh
      // error is a bare `Error` with no `response`, so callers lost the status
      // and body of the request they actually made and could only report a
      // generic failure. Matches the attempts-exhausted branch above.
      return Promise.reject(error);
    }

    return apiClient(originalRequest);
  },
);

export default apiClient;
